import React, { useState, useMemo, useEffect } from "react"
import { createPortal } from "react-dom"

import { useSelector } from "react-redux"
import { RootState } from "../../../../mp-core/redux/store"

import { Breakpoint, useTheme } from "@mui/material/styles"

import useMediaQuery from "@mui/material/useMediaQuery"

import GhostieCardContainer from "./GhostieCardContainer"
import { Box } from "@mui/material"
import { AnnouncementsItemType } from "../../../../mp-database/contextHelpManager"
import { useLocation, useParams } from "react-router-dom"
import { useStyles } from "./styles"
import {
  isAnnouncementWithLocations,
  isAnnouncementWithPrograms,
  SEPARATED_GHOSTIE_CARD_IDS,
  tipsTypes
} from "./helpers"

export const GHOSTIE_CARDS_CONTAINER = "GHOSTIE-CARDS-CONTAINER"

type ParamsType = {
  page: string
  module: string
  program: string
  subPage: string
}

interface BottomTipsPanelProps {}

const BottomTipsPanel: React.FC<
  React.PropsWithChildren<BottomTipsPanelProps>
> = props => {
  const theme = useTheme()
  const { classes, cx } = useStyles()

  const { page, module, program, subPage } = useParams<ParamsType>()
  const location = useLocation()

  const isStudyPlan =
    module === "study-plans" && (subPage === "view" || subPage === "edit")

  const isSnapLesson = location.pathname.startsWith("/snap/")

  const [selectedCard, setSelectedCard] = useState<null | number>(undefined)
  const [isVisible, setIsVisible] = useState(true)
  const [mainContentDOMNode, setMainContentDOMNode] =
    useState<HTMLElement>(null)

  const isSmallDevice = useMediaQuery(
    theme.breakpoints.down("md" as Breakpoint)
  )

  const { announcements } = useSelector((state: RootState) => ({
    announcements: state.users.announcements as AnnouncementsItemType[]
  }))

  const tipTypeData: Record<string, AnnouncementsItemType[]> = useMemo(
    () =>
      tipsTypes.reduce(
        (res, tip) => ({
          ...res,
          [tip.toLowerCase()]:
            announcements?.filter(item => {
              if (page === "login") {
                return item.type === tip
              } else {
                return (
                  item.type === tip &&
                  isAnnouncementWithLocations(item, page, module, subPage) &&
                  isAnnouncementWithPrograms(item, program)
                )
              }
            }) ?? []
        }),
        {}
      ),
    [announcements, module, program, subPage]
  )

  const listenToScroll = () => {
    let heightToHideFrom = 50
    const winScroll =
      document.body.scrollTop || document.documentElement.scrollTop

    if (winScroll > heightToHideFrom) {
      isVisible && setIsVisible(false)
    } else {
      setIsVisible(true)
    }
  }

  useEffect(() => {
    window.addEventListener("scroll", listenToScroll)

    return () => {
      window.removeEventListener("scroll", listenToScroll)
    }
  }, [isVisible])

  useEffect(() => {
    setMainContentDOMNode(document.querySelector("main"))
  }, [location.pathname])

  const ghostieCardsContainer = (
    <Box
      id={GHOSTIE_CARDS_CONTAINER}
      className={cx(classes.root, {
        [classes.invisibleAll]: !isVisible,
        [classes.layoutShift]: isStudyPlan && isVisible
      })}
      aria-hidden={!isVisible}
      {...(!isVisible && { inert: "" })}
    >
      {Object.entries(tipTypeData).map(([key, tipTypeDataItems], index) => {
        if (tipTypeDataItems.length <= 0) return
        if (subPage === "test-taking" || program === "masterylessons") return

        // This filter needs for separating of CUSTOM (Feedback) type announcement
        const filteredTipTypeDataItems = tipTypeDataItems.filter(
          tipTypeDataItem =>
            !SEPARATED_GHOSTIE_CARD_IDS.includes(tipTypeDataItem.id)
        )

        return (
          <GhostieCardContainer
            key={`${key}_${index}`}
            isHidden={!!(selectedCard && selectedCard !== index)}
            data={filteredTipTypeDataItems}
            type={key}
            // this prop is useful for mobile responsive view
            setSelectedCard={() => {
              if (isSmallDevice && selectedCard) {
                setTimeout(() => {
                  setSelectedCard(null)
                }, 300)
              } else {
                setSelectedCard(selectedCard === index ? null : index)
              }
            }}
          />
        )
      })}
    </Box>
  )

  if (isSnapLesson) return null

  return (
    mainContentDOMNode !== undefined &&
    (!!mainContentDOMNode
      ? createPortal(ghostieCardsContainer, mainContentDOMNode)
      : ghostieCardsContainer)
  )
}

export default BottomTipsPanel
